import clsx from "clsx";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { emitter, type MomentOverlayData } from "../util/momentOverlay.ts";
import {
	momentVariantStopsSim,
	PHASE_MOMENT_AUTO_ADVANCE_MS,
} from "../../../../common/phaseMoment.ts";
import { SafeHtml } from "./SafeHtml.tsx";
import { Confetti } from "./Confetti.tsx";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { toWorker } from "../util/toWorker.ts";
import { markCelebrationSeen } from "../util/achievementSession.ts";
import i18n from "../util/i18n.ts";

// No framer-motion here on purpose: its WAAPI tweens proved unreliable for
// this full-screen fixed overlay (first-trigger entry animation freezing
// mid-tween at ~0.46 opacity, AnimatePresence exit callbacks never firing →
// invisible position:fixed ghosts stacking up and blocking all touch input).
// Entry is a pure CSS keyframe animation applied on mount; exit is an exit
// class (opacity 0 + pointer-events:none) + a deterministic setTimeout that
// unmounts via React state. Keep it this way (see the G1a fix worklog entry
// before reintroducing any animation library here).
const EXIT_DURATION = 200;

// Buffer moments emitted before this component mounts (mirrors Notifications).
const initialMoments: MomentOverlayData[] = [];
const unbind = emitter.on("moment", (moment) => {
	initialMoments.push(moment);
});

// Mirror the persistent-notification behavior: stop game sim when a moment
// overlay appears (unless an autoPlayUntil run is already in progress). Closing
// does NOT resume — the player continues from the play menu themselves.
const stopGameSimForMoment = () => {
	toWorker("main", "getLocal", "autoPlayUntil").then((autoPlayUntil) => {
		if (!autoPlayUntil) {
			toWorker("main", "lockSet", ["stopGameSim", true]);
		}
	});
};

export const MobileMomentOverlay = () => {
	const [moment, setMoment] = useState<MomentOverlayData | undefined>(undefined);
	const [exiting, setExiting] = useState(false);
	// Mirror of the currently-shown moment so the emitter handler (set up once)
	// can decide whether to yield without a stale closure.
	const activeRef = useRef<MomentOverlayData | undefined>(undefined);

	useEffect(() => {
		unbind();

		const unbind2 = emitter.on("moment", (next) => {
			// A phase cutscene never interrupts a moment already on screen and never
			// queues (plan G1c: it is a passing beat, drop it). Celebration/somber
			// moments still replace whatever is showing.
			if (next.variant === "phase" && activeRef.current !== undefined) {
				return;
			}
			setExiting(false);
			setMoment(next);
		});

		if (initialMoments.length > 0) {
			setMoment(initialMoments[initialMoments.length - 1]);
			initialMoments.length = 0;
		}

		return unbind2;
	}, []);

	useEffect(() => {
		activeRef.current = moment;
	}, [moment]);

	const momentId = moment?.id;
	const momentVariant = moment?.variant;
	useEffect(() => {
		if (momentId === undefined || momentVariant === undefined) {
			return;
		}
		// Phase cutscenes intentionally do NOT lock the sim (plan G1c).
		if (momentVariantStopsSim(momentVariant)) {
			stopGameSimForMoment();
		}
	}, [momentId, momentVariant]);

	// Phase cutscene auto-advance: navigate to the CTA target and dismiss after a
	// short delay. The close button + CTA stay available as manual exits.
	useEffect(() => {
		if (
			momentId === undefined ||
			moment?.variant !== "phase" ||
			!moment.autoAdvance
		) {
			return;
		}
		const url = moment.ctaUrl;
		const timeoutID = setTimeout(() => {
			if (url) {
				realtimeUpdate([], url);
			}
			setExiting(true);
		}, moment.autoAdvanceMs ?? PHASE_MOMENT_AUTO_ADVANCE_MS);
		return () => {
			clearTimeout(timeoutID);
		};
	}, [momentId]);

	// Exit phase: after the CSS exit transition has had time to play, unmount
	// for real (React state → DOM removal). Never leave a fixed full-screen
	// element behind.
	useEffect(() => {
		if (!exiting || momentId === undefined) {
			return;
		}
		const timeoutID = setTimeout(() => {
			setExiting(false);
			setMoment(undefined);
		}, EXIT_DURATION);
		return () => {
			clearTimeout(timeoutID);
		};
	}, [exiting, momentId]);

	const close = () => {
		// G7-1: a closed celebration (championship / milestone) arms the home
		// "new achievement" prompt. This only records that the moment happened — it
		// does not touch the moment's own semantics (the prompt is appended after).
		if (moment?.variant === "celebration") {
			markCelebrationSeen();
		}
		setExiting(true);
	};

	const handleCta = () => {
		if (moment?.ctaUrl) {
			realtimeUpdate([], moment.ctaUrl);
		}
		close();
	};

	if (!moment) {
		return null;
	}

	const closeLabel = i18n.t("common.close", {
		ns: "ui",
		defaultValue: "Close",
	});

	const style = moment.teamColors
		? ({
				"--mobile-team-color-primary": moment.teamColors[0],
				"--mobile-team-color-secondary": moment.teamColors[1],
			} as CSSProperties)
		: undefined;

	return (
		<div
			// Remount per moment so the CSS entry animation replays for each one.
			key={moment.id}
			className={clsx(
				"mobile-moment-overlay",
				exiting && "mobile-moment-overlay-exit",
			)}
			data-variant={moment.variant}
			style={style}
		>
			{moment.variant === "celebration" ? (
				<Confetti
					colors={moment.teamColors}
					canvasId="moment-confetti"
					interactive={false}
				/>
			) : null}
			<button
				type="button"
				className="mobile-moment-overlay-close"
				aria-label={closeLabel}
				onClick={close}
			>
				&times;
			</button>
			<div className="mobile-moment-overlay-content">
				<h2 className="mobile-moment-overlay-title">{moment.title}</h2>
				{moment.subtitle ? (
					<div className="mobile-moment-overlay-subtitle">
						<SafeHtml
							dirty={moment.subtitle}
							htmlIsSafe={moment.subtitleHtmlIsSafe}
						/>
					</div>
				) : null}
				{moment.ctaText ? (
					<button
						type="button"
						className="mobile-moment-overlay-cta"
						onClick={handleCta}
					>
						{moment.ctaText}
					</button>
				) : null}
			</div>
		</div>
	);
};
